import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  boardListStateSchema,
  boardsPageSchema,
  ulidSchema,
  type ApiError,
  type BoardGroup,
  type BoardListState,
  type BoardPageBoard,
  type BoardsPage,
  type Workspace,
} from '@molt/shared';
import { get } from './api';
import { queryKeys } from './query-keys';

/**
 * The boards landing page's data layer — chunk `D2`, agent B (`FE-23.1`).
 *
 * ---------------------------------------------------------------------------
 * WHY THIS PAGE READS TWO ROUTES AND JOINS THEM CLIENT-SIDE
 * ---------------------------------------------------------------------------
 * `entry-surfaces.md` **D3**, and it is fixed. `GET /boards?state=open` is an
 * **INNER JOIN** boards→workspaces, so **a workspace with no boards produces no
 * group at all** — and "a workspace with no boards" is a state `FR-EXP-034`
 * names explicitly and `FR-PERM-004` AC2 asserts. `GET /workspaces` is the
 * route that knows about it. So the page calls both and left-joins.
 *
 * ⚠ **The ordering does not survive the join for free.** `GET /boards` orders
 * groups by `workspaces.created_at, workspaces.id`; `GET /workspaces` orders by
 * the same pair. Appending the workspaces that produced no group **in their own
 * order** therefore keeps one consistent sequence, and **sorting either result
 * alphabetically in the client breaks it silently** — in a way that only shows
 * up once a member has three workspaces, which no fixture has by default.
 * `joinWorkspaceGroups` sorts nothing, and its test has three workspaces, two
 * of them empty, for exactly that reason.
 */

// ---------------------------------------------------------------------------
// Endpoint
// ---------------------------------------------------------------------------

/**
 * `GET /boards?state=open|closed` — `requireUser`, no anonymous arm.
 *
 * ⚠ **A strict querystring**: any other key, or any other value, is a **422**,
 * not a silent fallback to `open`. Which is why the caller narrows first
 * (`boardListStateFrom`) rather than forwarding whatever the URL held.
 */
export function getBoardsPage(state: BoardListState): Promise<BoardsPage> {
  return get(`/boards?state=${encodeURIComponent(state)}`, boardsPageSchema);
}

export function boardsPageQueryOptions(
  state: BoardListState,
): UseQueryOptions<BoardsPage, ApiError> {
  return {
    queryKey: queryKeys.boards.page(state),
    queryFn: () => getBoardsPage(state),
  };
}

export function useBoardsPage(
  state: BoardListState,
): UseQueryResult<BoardsPage, ApiError> {
  return useQuery(boardsPageQueryOptions(state));
}

// ---------------------------------------------------------------------------
// The join, and the filter
// ---------------------------------------------------------------------------

/**
 * A group as the page renders it: a real group from `GET /boards`, or a
 * workspace from `GET /workspaces` that produced none.
 *
 * Structurally identical to `BoardGroup` on purpose — the page must not care
 * which of the two routes a heading came from.
 */
export interface LandingGroup {
  workspaceId: string;
  workspaceName: string;
  boards: BoardPageBoard[];
}

/**
 * Groups first, in the server's order; then every workspace that produced no
 * group, in `GET /workspaces`'s order, with an empty `boards`. **Sorts
 * nothing** — see the header.
 *
 * `workspaces` may be `undefined`: the workspaces query can still be in flight
 * when the boards query has resolved. Then the groups are returned alone.
 */
export function joinWorkspaceGroups(
  groups: readonly BoardGroup[],
  workspaces: readonly Workspace[] | undefined,
): LandingGroup[] {
  const joined: LandingGroup[] = groups.map((group) => ({
    workspaceId: group.workspaceId,
    workspaceName: group.workspaceName,
    boards: group.boards,
  }));

  if (workspaces === undefined) return joined;

  const groupedWorkspaceIds = new Set(groups.map((group) => group.workspaceId));
  for (const workspace of workspaces) {
    if (groupedWorkspaceIds.has(workspace.id)) continue;
    joined.push({ workspaceId: workspace.id, workspaceName: workspace.name, boards: [] });
  }

  return joined;
}

/**
 * `FR-NOTIF-029` AC4 / `entry-surfaces.md` **D9** — the name filter, which is
 * client-side over already-fetched groups and never a querystring (`GET
 * /boards` accepts only `state`; `?q=` is a 422).
 *
 * Case-insensitive over board titles. An empty or whitespace-only query returns
 * the input unchanged. It filters at **both** levels: a board whose title does
 * not match is removed from its group, and a group left with no boards is
 * removed entirely rather than left as an empty heading.
 *
 * ⚠ A group that has no boards at all is kept when the query is empty and
 * removed when it is not: an empty workspace matches no board name, and leaving
 * it in would make "no boards match" untrue on a page that still showed
 * headings.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THIS FILTERED ONLY GROUPS UNTIL D2'S COMPOSER FOUND IT, AND THE BUG WAS IN
 * THE BRIEF RATHER THAN IN THE IMPLEMENTATION
 * ---------------------------------------------------------------------------
 * `D2-BRIEF.md` §6.2's docstring specified group-level filtering alone and said
 * nothing about the boards inside a surviving group, so the leaf that built
 * this implemented the brief exactly and its unit test **pinned the bug** — an
 * assertion that the surviving group's `boards` array was *not touched*. Every
 * gate was green.
 *
 * The symptom, which only mounting the page could show: a workspace holding
 * *Roadmap* and *Q3 Budget*, filtered for `budget`, kept the group **and left
 * *Roadmap* on screen** — so the one thing `FR-NOTIF-029` AC4 asks for
 * (*"only boards whose name matches remain"*) was the one thing that did not
 * happen.
 *
 * Worth carrying past this file: a pure function's unit test is written from
 * the same sentence as the function, so when that sentence is wrong the test
 * agrees with the code and both are wrong together. Only a caller disagrees.
 */
export function filterGroupsByName(
  groups: readonly LandingGroup[],
  query: string,
): LandingGroup[] {
  const trimmed = query.trim();
  if (trimmed === '') return [...groups];

  const needle = trimmed.toLowerCase();
  return groups
    .map((group) => ({
      ...group,
      boards: group.boards.filter((board) => board.title.toLowerCase().includes(needle)),
    }))
    .filter((group) => group.boards.length > 0);
}

// ---------------------------------------------------------------------------
// Search-param narrowing — D1's finding 2, met for the second time
// ---------------------------------------------------------------------------

/**
 * ⚠ **`validateSearch` cannot STRIP a key it rejects.** TanStack Router builds
 * a match's search as `{ ...parentSearch, ...strictSearch }`, so `?state=17`
 * reaches Zod as the number `17`, is rejected, and **still arrives at
 * `useSearch()` as `17`**. D1 found this on `/sign-in` and recorded that every
 * route taking search params after it meets the same thing.
 *
 * **The declared type is not a runtime guarantee.** These two narrow at the
 * point of use, take `unknown` deliberately, and are tested against a number,
 * an array, `null`, `undefined`, `''` and a bogus string.
 */
export function boardListStateFrom(value: unknown): BoardListState {
  const parsed = boardListStateSchema.safeParse(value);
  return parsed.success ? parsed.data : 'open';
}

/** The workspace scope from `?workspace=` — a ULID, or `null` for "All workspaces". */
export function workspaceScopeFrom(value: unknown): string | null {
  const parsed = ulidSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}
